import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { switchMap, map, catchError, of, concatMap } from "rxjs";
import { ExternalUserManagementClient } from "src/api-service";
import { normalizeError } from "../../../../../app-store-error.types";
import { ExternalUsersActions } from "./external-users.actions";

@Injectable()
export class ExternalUsersEffects {
    private readonly actions$ = inject(Actions);
    private readonly externalUserClient = inject(ExternalUserManagementClient);

    loadExternalUsers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ExternalUsersActions.loadExternalUsers),
            switchMap(() =>
                this.externalUserClient.getAll().pipe(
                    map(users => ExternalUsersActions.loadExternalUsersSuccess({ users })),
                    catchError(error =>
                        of(
                            ExternalUsersActions.loadExternalUsersFailure({
                                error: normalizeError(error),
                                toastKey: "ADMIN_PANEL.EDIT_USERS.EXTERNAL.TOAST.LOAD_ERROR",
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    createExternalUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ExternalUsersActions.createExternalUser),
            switchMap(({ request }) =>
                this.externalUserClient.create(request).pipe(
                    map(user =>
                        ExternalUsersActions.createExternalUserSuccess({
                            user,
                            toastKey: "ADMIN_PANEL.EDIT_USERS.EXTERNAL.TOAST.CREATE_SUCCESS",
                        }),
                    ),
                    catchError(error =>
                        of(
                            ExternalUsersActions.createExternalUserFailure({
                                error: normalizeError(error),
                                toastKey: "ADMIN_PANEL.EDIT_USERS.EXTERNAL.TOAST.CREATE_ERROR",
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    updateExternalUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ExternalUsersActions.updateExternalUser),
            switchMap(({ request }) =>
                this.externalUserClient.update(request).pipe(
                    map(user =>
                        ExternalUsersActions.updateExternalUserSuccess({
                            user,
                            toastKey: "ADMIN_PANEL.EDIT_USERS.EXTERNAL.TOAST.UPDATE_SUCCESS",
                        }),
                    ),
                    catchError(error =>
                        of(
                            ExternalUsersActions.updateExternalUserFailure({
                                error: normalizeError(error),
                                toastKey: "ADMIN_PANEL.EDIT_USERS.EXTERNAL.TOAST.UPDATE_ERROR",
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    deleteExternalUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ExternalUsersActions.deleteExternalUser),
            switchMap(({ id }) =>
                this.externalUserClient.delete(id).pipe(
                    map(() =>
                        ExternalUsersActions.deleteExternalUserSuccess({
                            id,
                            toastKey: "ADMIN_PANEL.EDIT_USERS.EXTERNAL.TOAST.DELETE_SUCCESS",
                        }),
                    ),
                    catchError(error =>
                        of(
                            ExternalUsersActions.deleteExternalUserFailure({
                                error: normalizeError(error),
                                toastKey: "ADMIN_PANEL.EDIT_USERS.EXTERNAL.TOAST.DELETE_ERROR",
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    addContractToExternalUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ExternalUsersActions.addContractToExternalUser),
            concatMap(({ userId, contractId }) =>
                this.externalUserClient.addContract(userId, contractId).pipe(
                    map(user =>
                        ExternalUsersActions.addContractToExternalUserSuccess({
                            user,
                            toastKey: "ADMIN_PANEL.EDIT_USERS.EXTERNAL.TOAST.ADD_CONTRACT_SUCCESS",
                        }),
                    ),
                    catchError(error =>
                        of(
                            ExternalUsersActions.addContractToExternalUserFailure({
                                error: normalizeError(error),
                                toastKey: "ADMIN_PANEL.EDIT_USERS.EXTERNAL.TOAST.ADD_CONTRACT_ERROR",
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    removeContractFromExternalUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ExternalUsersActions.removeContractFromExternalUser),
            concatMap(({ userId, contractId }) =>
                this.externalUserClient.removeContract(userId, contractId).pipe(
                    map(user =>
                        ExternalUsersActions.removeContractFromExternalUserSuccess({
                            user,
                            toastKey: "ADMIN_PANEL.EDIT_USERS.EXTERNAL.TOAST.REMOVE_CONTRACT_SUCCESS",
                        }),
                    ),
                    catchError(error =>
                        of(
                            ExternalUsersActions.removeContractFromExternalUserFailure({
                                error: normalizeError(error),
                                toastKey: "ADMIN_PANEL.EDIT_USERS.EXTERNAL.TOAST.REMOVE_CONTRACT_ERROR",
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    changeExternalUserPassword$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ExternalUsersActions.changeExternalUserPassword),
            switchMap(({ request }) =>
                this.externalUserClient.changePassword(request).pipe(
                    map(() =>
                        ExternalUsersActions.changeExternalUserPasswordSuccess({
                            toastKey: "ADMIN_PANEL.EDIT_USERS.EXTERNAL.TOAST.CHANGE_PASSWORD_SUCCESS",
                        }),
                    ),
                    catchError(error =>
                        of(
                            ExternalUsersActions.changeExternalUserPasswordFailure({
                                error: normalizeError(error),
                                toastKey: "ADMIN_PANEL.EDIT_USERS.EXTERNAL.TOAST.CHANGE_PASSWORD_ERROR",
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );
}
